import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Chip,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  Send,
  Person,
  PhotoCamera,
  Favorite,
  Check,
  Delete,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  UserStatus,
  SilverConnectRegisterProfileRequest,
  Interest,
  Region,
  RegionDto,
  ProfileImageDto,
  PreferredAge,
  InterestsResponse,
  RegionsResponse,
  ProfileRegistrationResponse,
  PreferredRegionDto,
} from '../types';
import { apiClient, API_ENDPOINTS } from '../config/api';
import { uploadMultipleImages, UploadProgress } from '../utils/imageUpload';
import TermsAgreementSection from './Terms/TermsAgreementSection';
import type { TermsAgreementState } from '../types/terms';
import { useActiveTerms, useSubmitTermsAgreement } from '../hooks/useTerms';

interface FormData {
  // 기본 정보
  birthDate: string;
  gender: string;
  jobTitle: string;
  residenceRegion: RegionDto;
  oneLiner: string;
  selfIntro: string;
  
  // 사진
  profileImages: File[];
  
  // 선호조건
  preferredAge: PreferredAge;
  interestIds: number[];
  preferredRegions: PreferredRegionDto[];
}

interface FormErrors {
  [key: string]: string;
}

const ProfileRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  // 가입 타입 감지 (kakao: 카카오 가입, normal: 이메일 가입)
  const [isKakaoUser] = useState(() => {
    try {
      const data = sessionStorage.getItem('registrationUserData');
      return data ? JSON.parse(data).type === 'kakao' : false;
    } catch {
      return false;
    }
  });

  const steps = isKakaoUser
    ? ['약관 동의', '기본정보 입력', '프로필 사진 등록', '선호조건 설정']
    : ['기본정보 입력', '프로필 사진 등록', '선호조건 설정'];

  // 약관 동의 상태 (카카오 가입 시 사용)
  const [termsState, setTermsState] = useState<TermsAgreementState>({
    serviceTerms: false,
    privacyConsent: false,
    marketingConsent: false,
  });

  const { terms: activeTerms } = useActiveTerms();
  const { submit: submitTermsAgreement } = useSubmitTermsAgreement();

  // 스텝 관리
  const [activeStep, setActiveStep] = useState(0);
  
  // 폼 데이터
  const [formData, setFormData] = useState<FormData>({
    birthDate: '',
    gender: '',
    jobTitle: '',
    residenceRegion: { level1Id: 0, level1Name: '', level2Id: 0, level2Name: '' },
    oneLiner: '',
    selfIntro: '',
    profileImages: [],
    preferredAge: 'ANY',
    interestIds: [],
    preferredRegions: [],
  });

  // 에러 상태
  const [errors, setErrors] = useState<FormErrors>({});
  
  // 로딩 상태
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  
  // 메타 데이터
  const [interests, setInterests] = useState<Interest[]>([]);
  const [level1Regions, setLevel1Regions] = useState<Region[]>([]);
  const [level2Regions, setLevel2Regions] = useState<Region[]>([]);
  
  // 업로드 상태
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadedImages, setUploadedImages] = useState<ProfileImageDto[]>([]);

  // 성공 다이얼로그
  const [showSuccess, setShowSuccess] = useState(false);

  // 선호 지역 다이얼로그
  const [showAddRegionDialog, setShowAddRegionDialog] = useState(false);
  const [regionDialogLevel1Id, setRegionDialogLevel1Id] = useState(0);
  const [regionDialogLevel2Id, setRegionDialogLevel2Id] = useState(0);
  const [regionDialogLevel2Regions, setRegionDialogLevel2Regions] = useState<Region[]>([]);

  // 초기 체크
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.status === UserStatus.APPROVED) {
      navigate('/dashboard');
      return;
    }

    if (user.status === UserStatus.PENDING || user.status === UserStatus.REJECTED) {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  // 초기 메타 데이터 로드
  useEffect(() => {
    const loadMetaData = async () => {
      try {
        setMetaLoading(true);
        const [interestsRes, regionsRes] = await Promise.all([
          apiClient.get<InterestsResponse>(API_ENDPOINTS.META.INTERESTS),
          apiClient.get<RegionsResponse>(`${API_ENDPOINTS.META.REGIONS}?level=1`),
        ]);

        const activeInterests = interestsRes.result.interests
          .filter(interest => interest.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        
        const activeLevel1Regions = regionsRes.result.regions
          .filter(region => region.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        setInterests(activeInterests);
        setLevel1Regions(activeLevel1Regions);
      } catch (error) {
        console.error('메타 데이터 로딩 실패:', error);
      } finally {
        setMetaLoading(false);
      }
    };

    loadMetaData();
  }, []);

  // Level 2 지역 로드
  useEffect(() => {
    const loadLevel2Regions = async () => {
      if (formData.residenceRegion.level1Id > 0) {
        try {
          const response = await apiClient.get<RegionsResponse>(
            `${API_ENDPOINTS.META.REGIONS}?level=2&parentId=${formData.residenceRegion.level1Id}`
          );
          
          const activeLevel2Regions = response.result.regions
            .filter(region => region.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder);
          
          setLevel2Regions(activeLevel2Regions);
        } catch (error) {
          console.error('Level 2 지역 로딩 실패:', error);
        }
      } else {
        setLevel2Regions([]);
        setFormData(prev => ({
          ...prev,
          residenceRegion: { ...prev.residenceRegion, level2Id: 0 }
        }));
      }
    };

    loadLevel2Regions();
  }, [formData.residenceRegion.level1Id]);

  // 단계별 유효성 검사
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    // 카카오 가입: 약관 동의 스텝 (step 0)
    if (isKakaoUser && step === 0) {
      setErrors(newErrors);
      return termsState.serviceTerms && termsState.privacyConsent;
    }

    const contentStep = isKakaoUser ? step - 1 : step;
    switch (contentStep) {
      case 0: // 기본정보
        if (!formData.birthDate) newErrors.birthDate = '생년월일을 입력해주세요.';
        if (!formData.gender) newErrors.gender = '성별을 선택해주세요.';
        if (formData.residenceRegion.level1Id === 0) {
          newErrors.residenceRegion = '거주 지역을 선택해주세요.';
        }
        if (formData.residenceRegion.level2Id === 0) {
          newErrors.residenceRegion = '상세 지역을 선택해주세요.';
        }
        if (formData.oneLiner && (formData.oneLiner.length < 1 || formData.oneLiner.length > 64)) {
          newErrors.oneLiner = '한줄소개는 1~64자 사이여야 합니다.';
        }
        if (formData.selfIntro && (formData.selfIntro.length < 10 || formData.selfIntro.length > 512)) {
          newErrors.selfIntro = '자기소개는 10~512자 사이여야 합니다.';
        }
        break;
        
      case 1: // 사진
        if (formData.profileImages.length < 3) {
          newErrors.profileImages = '최소 3장의 사진을 추가해주세요.';
        } else if (formData.profileImages.length > 5) {
          newErrors.profileImages = '최대 5장까지만 추가할 수 있습니다.';
        }
        break;
        
      case 2: // 선호조건
        if (!formData.preferredAge) newErrors.preferredAge = '선호 나이를 선택해주세요.';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 다음 단계로
  const handleNext = async () => {
    if (!validateStep(activeStep)) return;

    // 카카오 가입: Step 0(약관 동의) 완료 시 즉시 서버에 약관 동의 전송
    if (isKakaoUser && activeStep === 0) {
      try {
        const findId = (type: string) => activeTerms.find((t) => t.type === type)?.id;
        const agreements = [
          { termsId: findId('SERVICE_TERMS')!, isAgreed: true },
          { termsId: findId('PRIVACY_CONSENT')!, isAgreed: true },
          { termsId: findId('MARKETING_CONSENT')!, isAgreed: termsState.marketingConsent },
        ].filter((a) => a.termsId != null);
        if (agreements.length > 0) {
          await submitTermsAgreement({ agreements });
        }
      } catch (err) {
        console.error('약관 동의 전송 실패:', err);
        // 실패해도 다음 단계 진행 허용 (handleSubmit에서 재시도)
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  // 이전 단계로
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // 파일 선택 처리 (1장 또는 여러장 추가)
  const handleFileAdd = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // 현재 이미지 수 + 새로 추가할 이미지 수가 5장을 초과하는지 체크
    const totalAfterAdd = formData.profileImages.length + files.length;
    if (totalAfterAdd > 5) {
      const maxCanAdd = 5 - formData.profileImages.length;
      setUploadError(`최대 5장까지만 가능합니다. 현재 ${formData.profileImages.length}장이 있어서 ${maxCanAdd}장만 더 추가할 수 있습니다.`);
      event.target.value = '';
      return;
    }

    // 중복 파일 제거
    const newFiles = files.filter(file => {
      const isDuplicate = formData.profileImages.some(existingFile => 
        existingFile.name === file.name && 
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified
      );
      return !isDuplicate;
    });

    if (newFiles.length === 0) {
      setUploadError('모든 사진이 이미 추가되어 있습니다.');
      event.target.value = '';
      return;
    }

    // 개별 파일 유효성 검사 (개수 체크 제외)
    const invalidFiles: string[] = [];
    newFiles.forEach((file, index) => {
      // 파일 크기 체크 (10MB)
      const MAX_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        invalidFiles.push(`사진 ${index + 1}: 파일 크기가 10MB를 초과합니다. (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
        return;
      }

      // MIME 타입 체크
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/webp',
        'image/heic', 'image/heif', 'image/avif', 'image/bmp',
        'image/gif', 'image/tiff', 'image/svg+xml'
      ];
      if (!allowedTypes.includes(file.type)) {
        invalidFiles.push(`사진 ${index + 1}: 지원하지 않는 파일 형식입니다. (${file.type})`);
        return;
      }
    });

    if (invalidFiles.length > 0) {
      setUploadError(invalidFiles.join('\n'));
      event.target.value = '';
      return;
    }

    // 기존 이미지에 새 파일들 추가
    setFormData(prev => ({
      ...prev,
      profileImages: [...prev.profileImages, ...newFiles]
    }));
    
    setUploadError('');
    
    // 파일 input 초기화
    event.target.value = '';
  };

  // 개별 파일 제거
  const handleFileRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      profileImages: prev.profileImages.filter((_, i) => i !== index)
    }));
    
    // 해당 인덱스의 업로드된 이미지도 제거
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };


  // 관심사 토글
  const handleInterestToggle = (interestId: number) => {
    setFormData(prev => ({
      ...prev,
      interestIds: prev.interestIds.includes(interestId)
        ? prev.interestIds.filter(id => id !== interestId)
        : [...prev.interestIds, interestId]
    }));
  };

  // 선호 지역 다이얼로그에서 Level1 선택 시 Level2 로드
  const handleRegionDialogLevel1Change = async (level1Id: number) => {
    setRegionDialogLevel1Id(level1Id);
    setRegionDialogLevel2Id(0);
    
    if (!level1Id) {
      setRegionDialogLevel2Regions([]);
      return;
    }

    try {
      const response = await apiClient.get<RegionsResponse>(
        `${API_ENDPOINTS.META.REGIONS}?level=2&parentId=${level1Id}`
      );
      
      const activeLevel2Regions = response.result.regions
        .filter(region => region.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      setRegionDialogLevel2Regions(activeLevel2Regions);
    } catch (err) {
      console.error('선호 지역 Level2 로드 실패:', err);
    }
  };

  // 선호 지역 추가
  const handleAddPreferredRegion = () => {
    if (!regionDialogLevel1Id || !regionDialogLevel2Id) {
      return;
    }

    // 선택한 지역의 이름 찾기
    const level1Region = level1Regions.find(r => r.id === regionDialogLevel1Id);
    const level2Region = regionDialogLevel2Regions.find(r => r.id === regionDialogLevel2Id);

    if (!level1Region || !level2Region) {
      return;
    }

    const newRegion: PreferredRegionDto = {
      level1Id: regionDialogLevel1Id,
      level2Id: regionDialogLevel2Id,
      level1Name: level1Region.nameKo,
      level2Name: level2Region.nameKo
    };

    // 중복 체크
    const isDuplicate = formData.preferredRegions.some(
      region => region.level1Id === newRegion.level1Id && region.level2Id === newRegion.level2Id
    );

    if (isDuplicate) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      preferredRegions: [...prev.preferredRegions, newRegion]
    }));

    // 다이얼로그 상태 초기화
    setShowAddRegionDialog(false);
    setRegionDialogLevel1Id(0);
    setRegionDialogLevel2Id(0);
    setRegionDialogLevel2Regions([]);
  };

  // 선호 지역 제거
  const handleRemovePreferredRegion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      preferredRegions: prev.preferredRegions.filter((_, i) => i !== index)
    }));
  };

  // 최종 제출
  const handleSubmit = async () => {
    if (!validateStep(steps.length - 1)) return;

    try {
      setLoading(true);

      let imagesToUpload = uploadedImages;

      // 사진이 아직 업로드되지 않았다면 지금 업로드
      if (formData.profileImages.length > 0 && uploadedImages.length === 0) {
        try {
          imagesToUpload = await uploadMultipleImages(
            formData.profileImages,
            (progresses) => setUploadProgress(progresses)
          );
          setUploadedImages(imagesToUpload);
        } catch (err: any) {
          console.error('이미지 업로드 실패:', err);
          setUploadError(err.message || '이미지 업로드에 실패했습니다.');
          setLoading(false);
          return;
        }
      }

      // 프로필 등록 요청 데이터 생성
      const requestData: SilverConnectRegisterProfileRequest = {
        birthDate: formData.birthDate,
        gender: formData.gender,
        jobTitle: formData.jobTitle || null,
        residenceRegion: formData.residenceRegion,
        oneLiner: formData.oneLiner || null,
        selfIntro: formData.selfIntro || null,
        preferredAge: formData.preferredAge,
        interestIds: formData.interestIds,
        preferredRegions: formData.preferredRegions,
        profileImages: imagesToUpload,
      };

      // 1단계: 프로필 저장 (본인인증 전 가능 — PENDING 전환 없음)
      const response = await apiClient.post<ProfileRegistrationResponse>(
        API_ENDPOINTS.PROFILE.REGISTER,
        requestData
      );

      if (response.resultType !== 'SUCCESS') {
        throw new Error(response.error?.message || '프로필 등록에 실패했습니다.');
      }

      // 카카오 가입: 약관 동의 재시도 (Step 0에서 이미 전송했으나 실패한 경우 대비)
      if (isKakaoUser) {
        const findId = (type: string) => activeTerms.find((t) => t.type === type)?.id;
        const agreements = [
          { termsId: findId('SERVICE_TERMS')!, isAgreed: true },
          { termsId: findId('PRIVACY_CONSENT')!, isAgreed: true },
          { termsId: findId('MARKETING_CONSENT')!, isAgreed: termsState.marketingConsent },
        ].filter((a) => a.termsId != null);
        if (agreements.length > 0) {
          await submitTermsAgreement({ agreements }).catch((err) => {
            console.error('약관 동의 재전송 실패:', err);
          });
        }
      }

      // 세션 스토리지 정리
      sessionStorage.removeItem('registrationUserData');
      sessionStorage.removeItem('kakaoUserData');

      // 2단계: 본인인증으로 이동 (인증 완료 후 /profile/submit 호출로 PENDING 전환)
      navigate('/identity-verification');
    } catch (error) {
      console.error('프로필 등록 실패:', error);
      setUploadError(error instanceof Error ? error.message : '프로필 등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 0단계: 약관 동의 (카카오 가입 전용)
  const renderTermsStep = () => (
    <Box>
      <Card sx={{ mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <CardContent>
          <Typography variant="h6" color="primary.main" gutterBottom>
            서비스 이용을 위한 약관에 동의해주세요
          </Typography>
          <Typography variant="body2" color="text.secondary">
            필수 약관에 동의하셔야 다음 단계로 진행할 수 있습니다.
          </Typography>
        </CardContent>
      </Card>
      <TermsAgreementSection value={termsState} onChange={setTermsState} />
    </Box>
  );

  // 1단계: 기본정보
  const renderBasicInfoStep = () => (
    <Box>
      <Card sx={{ mb: 4, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Person sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" color="primary.main">
              기본 정보를 입력해주세요
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            정확한 기본 정보는 매칭의 첫 걸음입니다! 💕<br />
            자기소개를 자세히 작성할수록 이성의 호감도가 높아져요! ✨
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            fullWidth
            label="생년월일"
            type="date"
            value={formData.birthDate}
            onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
            onMouseDown={(e) => {
              // 비어있을 때 마우스 클릭 시 1970-01-01로 설정
              if (!formData.birthDate) {
                setTimeout(() => {
                  setFormData(prev => ({ ...prev, birthDate: '1970-01-01' }));
                }, 0);
              }
            }}
            onTouchStart={(e) => {
              // 비어있을 때 터치 시 1970-01-01로 설정 (모바일)
              if (!formData.birthDate) {
                setTimeout(() => {
                  setFormData(prev => ({ ...prev, birthDate: '1970-01-01' }));
                }, 0);
              }
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 
              max: new Date().toISOString().split('T')[0], // 오늘 날짜까지만 선택 가능
              min: '1920-01-01' // 최소 1920년부터 선택 가능
            }}
            error={!!errors.birthDate}
            helperText={errors.birthDate}
            required
          />
          
          <FormControl fullWidth error={!!errors.gender} required>
            <FormLabel component="legend">성별</FormLabel>
            <RadioGroup
              value={formData.gender}
              onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
              row
            >
              <FormControlLabel value="MALE" control={<Radio />} label="남성" />
              <FormControlLabel value="FEMALE" control={<Radio />} label="여성" />
            </RadioGroup>
            {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
          </FormControl>
        </Box>

        <TextField
          fullWidth
          label="직업"
          value={formData.jobTitle}
          onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
          placeholder="예: 교사, 개발자, 프리랜서"
        />

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <FormControl fullWidth error={!!errors.residenceRegion} required>
            <InputLabel>거주 지역 (시/도)</InputLabel>
            <Select
              value={formData.residenceRegion.level1Id}
              onChange={(e) => {
                const selectedLevel1Id = Number(e.target.value);
                const selectedRegion = level1Regions.find(r => r.id === selectedLevel1Id);
                setFormData(prev => ({
                  ...prev,
                  residenceRegion: { 
                    level1Id: selectedLevel1Id, 
                    level1Name: selectedRegion?.nameKo || '',
                    level2Id: 0,
                    level2Name: ''
                  }
                }));
              }}
              label="거주 지역 (시/도)"
            >
              {level1Regions.map(region => (
                <MenuItem key={region.id} value={region.id}>
                  {region.nameKo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth error={!!errors.residenceRegion} required>
            <InputLabel>상세 지역</InputLabel>
            <Select
              value={formData.residenceRegion.level2Id}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                residenceRegion: { ...prev.residenceRegion, level2Id: Number(e.target.value) }
              }))}
              label="상세 지역"
              disabled={formData.residenceRegion.level1Id === 0}
            >
              {level2Regions.map(region => (
                <MenuItem key={region.id} value={region.id}>
                  {region.nameKo}
                </MenuItem>
              ))}
            </Select>
            {errors.residenceRegion && <FormHelperText>{errors.residenceRegion}</FormHelperText>}
          </FormControl>
        </Box>

        <TextField
          fullWidth
          label="한줄소개"
          value={formData.oneLiner}
          onChange={(e) => setFormData(prev => ({ ...prev, oneLiner: e.target.value }))}
          placeholder="나를 한줄로 표현해보세요"
          error={!!errors.oneLiner}
          helperText={errors.oneLiner || `${formData.oneLiner.length}/64자`}
          inputProps={{ maxLength: 64 }}
        />

        <TextField
          fullWidth
          multiline
          rows={4}
          label="자기소개"
          value={formData.selfIntro}
          onChange={(e) => setFormData(prev => ({ ...prev, selfIntro: e.target.value }))}
          placeholder="자신을 더 자세히 소개해주세요"
          error={!!errors.selfIntro}
          helperText={errors.selfIntro || `${formData.selfIntro.length}/512자`}
          inputProps={{ maxLength: 512 }}
        />
      </Box>
    </Box>
  );

  // 2단계: 사진 등록
  const renderPhotoStep = () => (
    <Box>
      <Card sx={{ mb: 4, backgroundColor: '#f0f8ff', border: '1px solid #b3daff' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PhotoCamera sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" color="primary.main">
              매력적인 프로필 사진을 올려보세요
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            자신의 얼굴이 잘 나온 사진으로 올려서 호감도를 높여보세요! 📸<br />
            밝고 자연스러운 미소가 담긴 사진이 가장 좋아요! 😊<br />
            최소 3장, 최대 5장까지 추가할 수 있습니다.
          </Typography>
        </CardContent>
      </Card>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}

      {errors.profileImages && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.profileImages}
        </Alert>
      )}

      {/* 사진 그리드 */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
        gap: 2,
        mb: 3 
      }}>
        {/* 추가된 사진들 */}
        {formData.profileImages.map((file, index) => (
          <Card key={index} sx={{ position: 'relative', aspectRatio: '1', overflow: 'hidden' }}>
            <Box
              component="img"
              src={URL.createObjectURL(file)}
              alt={`프로필 사진 ${index + 1}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: '50%',
                p: 0.5
              }}
            >
              <Delete
                sx={{ color: 'white', fontSize: 20, cursor: 'pointer' }}
                onClick={() => handleFileRemove(index)}
              />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                p: 1,
                textAlign: 'center'
              }}
            >
              <Typography variant="caption">
                {index + 1}번째 사진
              </Typography>
            </Box>
          </Card>
        ))}

        {/* 사진 추가 버튼 */}
        {formData.profileImages.length < 5 && (
          <Card 
            sx={{ 
              aspectRatio: '1', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '2px dashed #ccc',
              backgroundColor: '#fafafa',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f0f0f0',
                borderColor: '#999'
              }
            }}
            component="label"
          >
            <input
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/avif,image/bmp,image/gif,image/tiff,image/svg+xml"
              style={{ display: 'none' }}
              type="file"
              multiple
              onChange={handleFileAdd}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Add sx={{ fontSize: 40, color: '#999', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                사진 추가
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({formData.profileImages.length}/5)
              </Typography>
            </Box>
          </Card>
        )}
      </Box>

      {/* 상태 메시지 */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        {formData.profileImages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            첫 번째 사진을 추가해보세요<br />
            한 번에 여러 장 선택도 가능합니다
          </Typography>
        )}
        {formData.profileImages.length > 0 && formData.profileImages.length < 3 && (
          <Typography variant="body2" color="warning.main">
            최소 3장이 필요합니다. {3 - formData.profileImages.length}장 더 추가해주세요.
          </Typography>
        )}
        {formData.profileImages.length >= 3 && formData.profileImages.length <= 5 && (
          <Typography variant="body2" color="success.main">
            ✓ 사진이 준비되었습니다! 가입신청 완료 시 업로드됩니다.
          </Typography>
        )}
      </Box>

      {/* 업로드 진행 상황 */}
      {uploadProgress.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            업로드 진행상황
          </Typography>
          {uploadProgress.map((progress, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {progress.fileName}
                </Typography>
                <Typography variant="body2">
                  {progress.status === 'completed' ? '완료' : `${progress.progress}%`}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress.progress}
                color={progress.status === 'error' ? 'error' : 'primary'}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* 업로드 완료된 이미지 */}
      {uploadedImages.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {uploadedImages.length}장의 사진이 성공적으로 업로드되었습니다.
        </Alert>
      )}
    </Box>
  );

  // 3단계: 선호조건
  const renderPreferencesStep = () => (
    <Box>
      <Card sx={{ mb: 4, backgroundColor: '#fff0f5', border: '1px solid #ffcdd2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Favorite sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" color="primary.main">
              이상적인 만남을 위한 선호 조건을 설정해보세요
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            세심하게 설정한 선호 조건이 더 나은 매칭을 만들어드려요! 💝<br />
            관심사가 비슷할수록 대화가 더욱 즐거워집니다! 🎯
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl component="fieldset" fullWidth error={!!errors.preferredAge} required>
          <FormLabel component="legend">선호 나이</FormLabel>
          <RadioGroup
            value={formData.preferredAge}
            onChange={(e) => setFormData(prev => ({ ...prev, preferredAge: e.target.value as PreferredAge }))}
          >
            <FormControlLabel value="YOUNGER" control={<Radio />} label="연하" />
            <FormControlLabel value="OLDER" control={<Radio />} label="연상" />
            <FormControlLabel value="ANY" control={<Radio />} label="상관없음" />
          </RadioGroup>
          {errors.preferredAge && <FormHelperText>{errors.preferredAge}</FormHelperText>}
        </FormControl>

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            관심사
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            공통 관심사로 더 깊은 대화를 나눠보세요
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {interests.map(interest => (
              <Chip
                key={interest.id}
                label={interest.labelKo}
                clickable
                color={formData.interestIds.includes(interest.id) ? 'primary' : 'default'}
                onClick={() => handleInterestToggle(interest.id)}
                variant={formData.interestIds.includes(interest.id) ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ border: '2px dashed #e0e0e0', borderRadius: 2, p: 3, backgroundColor: '#fafafa' }}>
          <Typography variant="subtitle1" gutterBottom>
            선호 지역 (선택사항)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            만나고 싶은 상대방의 거주 지역을 선택해주세요 (최대 5개)
          </Typography>

          {/* 선호 지역 추가 버튼 */}
          <Button
            variant="outlined"
            onClick={() => setShowAddRegionDialog(true)}
            disabled={formData.preferredRegions.length >= 5}
            sx={{ 
              mb: 2,
              height: 56,
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: 'primary.main',
              color: 'primary.main',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'primary.50',
                borderColor: 'primary.dark',
              },
              '&:disabled': {
                borderColor: 'grey.300',
                color: 'grey.400',
              }
            }}
            fullWidth
          >
            + 선호 지역 추가하기
          </Button>

          {/* 추가된 지역들 */}
          {formData.preferredRegions.length > 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                선택된 지역: {formData.preferredRegions.length}/5
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.preferredRegions.map((region, index) => (
                  <Chip
                    key={`${region.level1Id}-${region.level2Id}`}
                    label={region.level1Name && region.level2Name ? 
                      `${region.level1Name} ${region.level2Name}` : 
                      '지역 정보 없음'
                    }
                    onDelete={() => handleRemovePreferredRegion(index)}
                    variant="filled"
                    color="primary"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                아직 선택된 지역이 없습니다<br />
                위 버튼을 클릭하여 선호 지역을 추가해보세요
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  if (metaLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography>메타 정보를 불러오는 중...</Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          프로필 등록
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
          멋진 인연을 위한 첫 걸음을 시작해보세요
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 400 }}>
          {isKakaoUser && activeStep === 0 && renderTermsStep()}
          {activeStep === (isKakaoUser ? 1 : 0) && renderBasicInfoStep()}
          {activeStep === (isKakaoUser ? 2 : 1) && renderPhotoStep()}
          {activeStep === (isKakaoUser ? 3 : 2) && renderPreferencesStep()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBefore />}
          >
            이전
          </Button>

          <Box sx={{ flex: 1 }} />

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={<Send />}
            >
              {loading ? '등록 중...' : '가입신청완료'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NavigateNext />}
              disabled={isKakaoUser && activeStep === 0 && (!termsState.serviceTerms || !termsState.privacyConsent)}
            >
              다음
            </Button>
          )}
        </Box>

        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              프로필을 등록하는 중입니다...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* 성공 다이얼로그 */}
      <Dialog open={showSuccess} onClose={() => setShowSuccess(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Check color="success" sx={{ fontSize: 64 }} />
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
            {user?.name}님, 가입신청이 성공적으로 완료되었습니다!
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            관리자가 심사하고 결과를 <strong>{user?.email}</strong>로 알려드리겠습니다.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            • 심사는 보통 1-2일 소요됩니다.<br />
            • 승인 완료 시 이메일로 상세한 안내를 보내드립니다.<br />
            • 승인 후 10개의 하트가 지급됩니다.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowSuccess(false);
              navigate('/dashboard');
            }}
          >
            대시보드로 이동
          </Button>
        </DialogActions>
      </Dialog>

      {/* 선호 지역 추가 다이얼로그 */}
      <Dialog 
        open={showAddRegionDialog} 
        onClose={() => {
          setShowAddRegionDialog(false);
          setRegionDialogLevel1Id(0);
          setRegionDialogLevel2Id(0);
          setRegionDialogLevel2Regions([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>선호 지역 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>시/도</InputLabel>
              <Select
                value={regionDialogLevel1Id}
                label="시/도"
                onChange={(e) => handleRegionDialogLevel1Change(Number(e.target.value))}
              >
                <MenuItem value={0}>선택해주세요</MenuItem>
                {level1Regions.map((region) => (
                  <MenuItem key={region.id} value={region.id}>
                    {region.nameKo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>구/군</InputLabel>
              <Select
                value={regionDialogLevel2Id}
                label="구/군"
                onChange={(e) => setRegionDialogLevel2Id(Number(e.target.value))}
                disabled={!regionDialogLevel1Id}
              >
                <MenuItem value={0}>선택해주세요</MenuItem>
                {regionDialogLevel2Regions.map((region) => (
                  <MenuItem key={region.id} value={region.id}>
                    {region.nameKo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowAddRegionDialog(false);
              setRegionDialogLevel1Id(0);
              setRegionDialogLevel2Id(0);
              setRegionDialogLevel2Regions([]);
            }}
          >
            취소
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddPreferredRegion}
            disabled={!regionDialogLevel1Id || !regionDialogLevel2Id}
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfileRegistrationPage;